import React, { useState, useEffect, useCallback } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { partnerAccountingService, REVERSEMENT_STATUSES, OPERATION_TYPES } from '../../services/partnerAccountingService';
import { providerService } from '../../services/providerService';
import PartnerSummaryTable from './components/PartnerSummaryTable';
import PartnerOperationForm from './components/PartnerOperationForm';
import ReversementModal from './components/ReversementModal';
import { DollarSign, AlertTriangle, CheckCircle, Plus, ArrowDownLeft, RefreshCw, Clock, Banknote, Users, BarChart3, X } from 'lucide-react';
import Icon from '../../components/AppIcon';


const STATUS_COLORS = {
  a_reverser: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
  partiellement_reverse: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  solde: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
};
const STATUS_LABELS = { a_reverser: 'À reverser', partiellement_reverse: 'Partiellement reversé', solde: 'Soldé' };

export default function PartnerAccounting() {
  const [tab, setTab] = useState('overview'); // overview | operations | alerts
  const [summary, setSummary] = useState(null);
  const [providerSummaries, setProviderSummaries] = useState([]);
  const [operations, setOperations] = useState([]);
  const [overdueOps, setOverdueOps] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showOpForm, setShowOpForm] = useState(false);
  const [showReversement, setShowReversement] = useState(false);
  const [reversementTarget, setReversementTarget] = useState(null);
  const [editingOp, setEditingOp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProvider, setFilterProvider] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, bySup, ops, overdue, provs] = await Promise.all([
        partnerAccountingService?.getGlobalSummary(),
        partnerAccountingService?.getSummaryByProvider(),
        partnerAccountingService?.getOperations({ status: filterStatus || undefined }),
        partnerAccountingService?.getOverdueOperations(3),
        providerService?.getAll({ activeOnly: true }),
      ]);
      setSummary(sum);
      setProviderSummaries(bySup);
      setOperations(ops);
      setOverdueOps(overdue);
      setProviders(provs);
    } catch (err) {
      console.error('Error loading partner accounting:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSuccess = () => {
    setShowOpForm(false);
    setShowReversement(false);
    setEditingOp(null);
    setReversementTarget(null);
    loadData();
  };

  const filteredOps = operations?.filter(op => {
    if (filterProvider && op?.providerId !== filterProvider && op?.providerName !== filterProvider) return false;
    return true;
  });

  const kpiCards = [
    {
      title: 'Total encaissé par prestataires',
      value: `${Number(summary?.total_collected_by_partners || 0)?.toFixed(2)}€`,
      icon: DollarSign,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
    },
    {
      title: 'Parts prestataires',
      value: `${Number(summary?.total_partner_shares || 0)?.toFixed(2)}€`,
      icon: Users,
      color: 'text-orange-600',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
    },
    {
      title: 'Total dû à ADT',
      value: `${Number(summary?.total_due_to_adt || 0)?.toFixed(2)}€`,
      icon: BarChart3,
      color: 'text-purple-600',
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-800',
    },
    {
      title: 'Déjà reversé à ADT',
      value: `${Number(summary?.total_reversed_to_adt || 0)?.toFixed(2)}€`,
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
    },
    {
      title: 'Solde restant à reverser',
      value: `${Number(summary?.total_remaining_balance || 0)?.toFixed(2)}€`,
      icon: Banknote,
      color: 'text-red-600',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
    },
    {
      title: 'Opérations en retard',
      value: `${Number(summary?.overdue_operations || 0)}`,
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Comptabilité Partenaires
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Suivi des encaissements, reversements et soldes par prestataire
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              className="p-2 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title="Actualiser"
            >
              <RefreshCw className="h-4 w-4 text-gray-500" />
            </button>
            <button
              onClick={() => { setEditingOp(null); setShowOpForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Nouvelle opération
            </button>
          </div>
        </div>

        {/* Alerts Banner */}
        {overdueOps?.length > 0 && (
          <div className="mb-5 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                {overdueOps?.length} opération(s) en retard de reversement (&gt; 3 jours)
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                {overdueOps?.map(op => `${op?.providerName} — ${op?.remainingBalance?.toFixed(2)}€`)?.join(' · ')}
              </p>
            </div>
            <button onClick={() => setTab('alerts')} className="ml-auto text-xs text-amber-700 dark:text-amber-400 underline whitespace-nowrap">
              Voir les alertes
            </button>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {kpiCards?.map(card => {
            const Icon = card?.icon;
            return (
              <div key={card?.title} className={`${card?.bg} rounded-xl border ${card?.border} p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`h-5 w-5 ${card?.color}`} />
                </div>
                <p className={`text-xl font-bold ${card?.color}`}>{card?.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-tight">{card?.title}</p>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-gray-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
          {[
            { id: 'overview', label: 'Vue par prestataire' },
            { id: 'operations', label: 'Toutes les opérations' },
            { id: 'alerts', label: `Alertes${overdueOps?.length > 0 ? ` (${overdueOps?.length})` : ''}` },
          ]?.map(t => (
            <button
              key={t?.id}
              onClick={() => setTab(t?.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t?.id
                  ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t?.label}
            </button>
          ))}
        </div>

        {/* Tab: Overview */}
        {tab === 'overview' && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">Tableau par prestataire</h2>
              <span className="text-sm text-gray-400">{providerSummaries?.length} prestataire(s)</span>
            </div>
            <PartnerSummaryTable
              data={providerSummaries}
              loading={loading}
              onSelectProvider={row => {
                setSelectedProvider(row);
                setFilterProvider(row?.providerId || row?.providerName);
                setTab('operations');
              }}
            />
          </div>
        )}

        {/* Tab: Operations */}
        {tab === 'operations' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les statuts</option>
                {REVERSEMENT_STATUSES?.map(s => (
                  <option key={s?.value} value={s?.value}>{s?.label}</option>
                ))}
              </select>
              <select
                value={filterProvider}
                onChange={e => setFilterProvider(e?.target?.value)}
                className="px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les prestataires</option>
                {providerSummaries?.map(p => (
                  <option key={p?.providerId || p?.providerName} value={p?.providerId || p?.providerName}>
                    {p?.providerName}
                  </option>
                ))}
              </select>
              {(filterStatus || filterProvider) && (
                <button
                  onClick={() => { setFilterStatus(''); setFilterProvider(''); setSelectedProvider(null); }}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-4 w-4" />
                  Effacer filtres
                </button>
              )}
            </div>

            {/* Operations Table */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Opérations partenaires
                  {selectedProvider && <span className="ml-2 text-blue-600 text-sm">— {selectedProvider?.providerName}</span>}
                </h2>
                <span className="text-sm text-gray-400">{filteredOps?.length} opération(s)</span>
              </div>
              {loading ? (
                <div className="p-8 text-center text-gray-400">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Chargement...
                </div>
              ) : filteredOps?.length === 0 ? (
                <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                  <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Aucune opération trouvée</p>
                  <button
                    onClick={() => setShowOpForm(true)}
                    className="mt-3 text-sm text-blue-600 hover:underline"
                  >
                    + Ajouter une opération
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-slate-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Prestataire</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Client</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Type</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Encaissé</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Part prest.</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Dû ADT</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Reversé</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Solde</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-500 dark:text-gray-400">Statut</th>
                        <th className="py-3 px-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                      {filteredOps?.map(op => (
                        <tr key={op?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                          <td className="py-3 px-4 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {op?.operationDate ? new Date(op?.operationDate)?.toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{op?.providerName}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{op?.clientName || '—'}</td>
                          <td className="py-3 px-4 text-gray-500 dark:text-gray-400">
                            {OPERATION_TYPES?.find(t => t?.value === op?.operationType)?.label || op?.operationType}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900 dark:text-white">
                            {op?.amountCollectedByPartner?.toFixed(2)}€
                          </td>
                          <td className="py-3 px-4 text-right text-orange-600">
                            {op?.partnerShare?.toFixed(2)}€
                          </td>
                          <td className="py-3 px-4 text-right text-blue-600 font-medium">
                            {op?.amountDueToAdt?.toFixed(2)}€
                          </td>
                          <td className="py-3 px-4 text-right text-green-600">
                            {op?.amountReversedToAdt?.toFixed(2)}€
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-gray-900 dark:text-white">
                            {op?.remainingBalance?.toFixed(2)}€
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS?.[op?.reversementStatus] || ''}`}>
                              {STATUS_LABELS?.[op?.reversementStatus] || op?.reversementStatus}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {op?.reversementStatus !== 'solde' && (
                              <button
                                onClick={() => {
                                  setReversementTarget({
                                    providerId: op?.providerId,
                                    providerName: op?.providerName,
                                    remainingBalance: op?.remainingBalance,
                                  });
                                  setShowReversement(true);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors whitespace-nowrap"
                              >
                                <ArrowDownLeft className="h-3 w-3" />
                                Reversement
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Alerts */}
        {tab === 'alerts' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-slate-700">
                <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Opérations en retard de reversement
                </h2>
                <p className="text-sm text-gray-400 mt-1">Opérations non soldées depuis plus de 3 jours</p>
              </div>
              {overdueOps?.length === 0 ? (
                <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                  <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-400 opacity-60" />
                  <p className="text-sm">Aucune opération en retard. Tout est à jour !</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-slate-700">
                  {overdueOps?.map(op => {
                    const daysOverdue = Math.floor(
                      (new Date() - new Date(op?.operationDate)) / (1000 * 60 * 60 * 24)
                    );
                    return (
                      <div key={op?.id} className="p-4 flex items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <Clock className="h-4 w-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {op?.providerName}
                              {op?.clientName && <span className="text-gray-400 font-normal"> — {op?.clientName}</span>}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {OPERATION_TYPES?.find(t => t?.value === op?.operationType)?.label} ·{' '}
                              {op?.operationDate ? new Date(op?.operationDate)?.toLocaleDateString('fr-FR') : '—'} ·{' '}
                              <span className="text-amber-600 font-medium">{daysOverdue} jours de retard</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Solde dû</p>
                            <p className="font-bold text-red-600">{op?.remainingBalance?.toFixed(2)}€</p>
                          </div>
                          <button
                            onClick={() => {
                              setReversementTarget({
                                providerId: op?.providerId,
                                providerName: op?.providerName,
                                remainingBalance: op?.remainingBalance,
                              });
                              setShowReversement(true);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap"
                          >
                            <ArrowDownLeft className="h-3 w-3" />
                            Enregistrer reversement
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Modals */}
      {showOpForm && (
        <PartnerOperationForm
          providers={providers}
          initialData={editingOp}
          onClose={() => { setShowOpForm(false); setEditingOp(null); }}
          onSuccess={handleSuccess}
        />
      )}
      {showReversement && reversementTarget && (
        <ReversementModal
          provider={reversementTarget}
          onClose={() => { setShowReversement(false); setReversementTarget(null); }}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
